import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback
} from 'react';
import axiosInstance from '../api/axiosInstance';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* =========================
     VERIFY TOKEN
  ========================= */
  const verifyToken = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/verifytoken');

      if (response.data?.status === 200) {
        setUser(response.data.data.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token verification failed');
      return false;
    }
  }, []);

  /* =========================
     INIT AUTH
  ========================= */
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      const isValid = await verifyToken();
      if (!isValid) {
        logout();
      }

      setLoading(false);
    };

    initAuth();
  }, [verifyToken]);

  /* =========================
     LOGIN
  ========================= */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password,
      });

      if (response.data?.status === 200) {
        const { token, user } = response.data.data;

        localStorage.setItem('token', token);
        setUser(user);

        return { success: true };
      }

      throw new Error('Login failed');
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Login failed. Please try again.';

      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     LOGOUT
  ========================= */
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        error,
        verifyToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
